import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Plus, Pencil, Power, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PaymentPolicyField, { inheritedPolicy } from "@/components/admin/PaymentPolicyField";

const empty = { name: "", description: "", price: "", duration: 30, active: true, payment_policy: inheritedPolicy };

export default function ServicesTab({ services, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, description: s.description || "", price: s.price ?? "", duration: s.duration, active: s.active, payment_policy: s.payment_policy || inheritedPolicy });
    setEditId(s.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do serviço.");
    if (!form.duration || form.duration < 5) return toast.error("Informe a duração em minutos (mín. 5).");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      price: form.price === "" ? null : Number(form.price),
      duration: Number(form.duration),
      active: form.active,
      payment_policy: form.payment_policy,
    };
    try {
      if (editId) await api.put(`/admin/services/${editId}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editId ? "Serviço atualizado." : "Serviço cadastrado.");
      setOpen(false); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/services/${s.id}`, {
        name: s.name, description: s.description, price: s.price, duration: s.duration, active: !s.active, payment_policy: s.payment_policy,
      });
      reload();
    } catch { toast.error("Erro ao alterar status."); }
  };

  const remove = async (s) => {
    try {
      const { data } = await api.delete(`/admin/services/${s.id}`);
      toast.success(data.deleted ? "Serviço excluído." : data.message);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir."); }
  };

  return (
    <div className="fade-in">
      <Header title="Serviços" onNew={openNew} testid="new-service-btn" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {services.map((s) => (
          <div key={s.id} data-testid={`service-item-${s.id}`} className={`card-surface p-5 ${!s.active ? "opacity-55" : ""}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-sans2 font-semibold text-amber-50">{s.name}</h3>
                {s.description && <p className="text-sm text-stone-400 mt-0.5">{s.description}</p>}
                <div className="flex items-center gap-3 mt-2 font-mono2 text-sm">
                  <span className="text-stone-300">{s.duration} min</span>
                  {s.price_fmt && <span className="text-[#D4AF37]">{s.price_fmt}</span>}
                  {!s.active && <span className="text-xs text-stone-500">(inativo)</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <IconBtn testid={`edit-service-${s.id}`} onClick={() => openEdit(s)} icon={Pencil} label="Editar" />
              <IconBtn testid={`toggle-service-${s.id}`} onClick={() => toggleActive(s)} icon={Power} label={s.active ? "Desativar" : "Ativar"} />
              <IconBtn testid={`delete-service-${s.id}`} onClick={() => remove(s)} icon={Trash2} label="Excluir" danger />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#12141A] border-[#282C37]">
          <DialogHeader><DialogTitle className="font-display text-amber-100">{editId ? "Editar serviço" : "Novo serviço"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Nome">
              <input data-testid="service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Descrição (opcional)">
              <input data-testid="service-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Preço R$ (opcional)">
                <input data-testid="service-price-input" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Duração (min)">
                <input data-testid="service-duration-input" type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <PaymentPolicyField value={form.payment_policy} onChange={(payment_policy) => setForm({ ...form, payment_policy })} />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-stone-400 font-sans2">Cancelar</button>
            <button data-testid="save-service-btn" onClick={save} disabled={saving} className={saveCls}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const inputCls = "w-full bg-[#0A0B0E] border border-[#282C37] rounded-lg px-3 py-2 text-amber-50 focus:border-[#D4AF37] focus:outline-none font-sans2 text-sm";
export const saveCls = "px-4 py-2 text-sm bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0B0E] rounded-lg font-sans2 font-semibold flex items-center gap-2 disabled:opacity-60";

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm text-stone-300 font-sans2 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

export function Header({ title, onNew, testid }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h1 className="font-display text-2xl sm:text-3xl gold-text">{title}</h1>
      {onNew && (
        <button data-testid={testid} onClick={onNew} className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0B0E] px-4 py-2 rounded-lg font-sans2 font-semibold text-sm transition-colors">
          <Plus className="w-4 h-4" /> Novo
        </button>
      )}
    </div>
  );
}

function IconBtn({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-sans2 transition-colors ${danger ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-[#282C37] text-stone-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
